import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { Utilities } from 'src/app/class/utilities';
import { Part } from 'src/app/interface/part';
import { LayoutService } from 'src/app/service/layout.service';
import { PartService } from 'src/app/service/part.service';
import { MoneyDirective } from 'src/app/shared/directives/money.directive';
import { GetRowIndexPipe } from './pipes/get-row-index.pipe';
import { MatError } from '@angular/material/form-field';
import { ErrorPipe } from 'src/app/shared/pipes/error.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-validate-ncm-before-closing-os',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    TranslateModule,
    MoneyDirective,
    GetRowIndexPipe,
    ErrorPipe,

    MatDialogTitle,
    MatTooltip,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatError,
    MatDivider,
  ],
  templateUrl: './validate-ncm-before-closing-os.component.html',
  styleUrls: ['./validate-ncm-before-closing-os.component.scss'],
})
export class ValidateNcmBeforeClosingOsComponent implements OnInit, OnDestroy {
  @ViewChild(MatTooltip) tooltip: MatTooltip;
  private _destroy$ = new Subject<void>();

  form = new FormGroup({
    items: new FormArray([])
  });
  public invalidParts: Part[] = [];
  baseColumns = ['name', 'description', 'ncm', 'newNcm'];
  columns = ['name', 'description', 'ncm', 'newNcm'] ;
  partsChangeNcm: partsChangeNcm[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    protected dialogRef: MatDialogRef<ValidateNcmBeforeClosingOsComponent>,
    private _partService: PartService,
    public layout: LayoutService
  ) {

  }

  ngOnInit() {
    this.invalidParts = this.data.invalidParts;
    this.invalidParts.forEach(part => this.form.controls.items.push(this._createPartFormGroup(part)));

    this.layout.isHandset$.subscribe(isMobile => {
      if (isMobile && this.columns.includes("ncm")) {
        this.columns.splice(2, 2);
      } else {
        this.columns = this.baseColumns;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _createPartFormGroup(part: Part): FormGroup {
    return new FormGroup({
      id: new FormControl(part.id),
      NCM: new FormControl(part.NCM ?? "", [Utilities.ncmValidator(), Validators.required])
    });
  }

  async save() {
    try{
      let partsFromForm = this.form.controls.items.getRawValue();

      partsFromForm = partsFromForm.map(partFromForm => {
        const registeredPart = this.invalidParts.find(part => part.id === partFromForm.id);
        return {
          ...registeredPart,
          NCM: partFromForm.NCM
        }
      })

      const partsToUpdate: Part[] = [];
      for (const partFromForm of partsFromForm) {
        const partFull = await this._partService.get(partFromForm.id);
        const partShouldBeUpdated = (partFromForm.NCM?.toString()?.length === 8) 
                                    && !Utilities.stringHaveOnlyEqualCharacters(partFromForm.NCM?.toString());
        const alreadyIncluded = partsToUpdate.find(part => part.id === partFull.id);

        if (partShouldBeUpdated && !alreadyIncluded) {
          partsToUpdate.push({
            ...partFull,
            NCM: partFromForm.NCM
          })
        }
      }

      if (partsToUpdate.length) {
        await this._partService.addOrUpdateMany(partsToUpdate, false);
      }

      this.dialogRef.close(partsToUpdate);
    } catch (error) {
        console.error(error)
    } finally {
      this.layout.loader = true;
    }
  }

  showTooltip() {
    this.tooltip.show();
  }

  get closeOsDialogTooltip(): string {
    return 'Alguma(s) peça(s) na OS contém NCM inválido. \nPara continuar o processo de fechamento da OS é necessário corrigir o NCM da(s) mesma(s).';
  }
}

export interface partsChangeNcm {
  part: Part;
  newNcm: number;
}
